import { styled } from 'dripsy';
import React, { Fragment, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useWindowDimensions, View } from 'react-native';
import { ModalComponentProp, useModal } from 'react-native-modalfy';
import { observer } from 'mobx-react-lite';

import { formatCurrencyToGBP, parseApiError } from '@utils';
import { BaseText } from '../common/BaseText';
import { IconTouchable } from '../common/IconTouchable';
import { ModalView } from './components/ModalView';
import { Separator } from '../common/Separator';
import { Loader } from '../common/Loader';
import { ScrollView } from '../common/ScrollView';
import { CouponForm } from '../form/CouponForm';
import { EmptyResultView } from '../views/EmptyResultView';
import { SubscriptionPlansLayout } from '../layouts/SubscriptionPlansLayout';
import {
  SubscriptionPlanItem,
  SubscriptionPlanItemStyleEnum,
} from '../business/SubscriptionPlanItem';
import { ModalStackParamsType } from '../../modals/types';
import { TariffPlanCodeEnum } from '@constants';
import {
  useAsyncInit,
  useCouponCodeStore,
  useSubscriptionPlansLayout,
} from '@hooks';
import { ToastService } from '@services';
import { useViewerModel } from '@models/Viewer/ViewerModel';

const ScrollViewContainer = styled(ScrollView)({
  px: '$8',
});

const TopContainer = styled(View)({
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
});

const CouponFormContainer = styled(View)({
  maxWidth: 350,
});

const Title = styled(BaseText)({
  color: '$heading800',
  variant: 'text.xlarge',
  fontWeight: 'semi',
});

const Description = styled(BaseText)({
  color: '$text400',
  variant: 'text.medium',
});

const LoaderContainer = styled(View)({
  flex: [1, 0],
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: 360,
  minWidth: 500,
});

const EmptyViewContainer = styled(LoaderContainer)({});

const MONTHLY_PLAN = {
  style: SubscriptionPlanItemStyleEnum.Choose,
  code: TariffPlanCodeEnum.Monthly,
};

const QUATERLY_PLAN = {
  style: SubscriptionPlanItemStyleEnum.Choose,
  code: TariffPlanCodeEnum.Quarterly,
};

const YEARLY_PLAN = {
  style: SubscriptionPlanItemStyleEnum.Promote,
  code: TariffPlanCodeEnum.Yearly,
};

const SUBSCRIPTIONS = [MONTHLY_PLAN, QUATERLY_PLAN, YEARLY_PLAN];

export interface IChangeSubscriptionPlanModalParams {
  onSelect(item: TariffPlanCodeEnum, couponId?: number): void;
}

type ModalPropsType = ModalComponentProp<
  ModalStackParamsType,
  void,
  'ChangeSubscriptionPlanModal'
>;

export const ChangeSubscriptionPlanModal = observer(
  ({ modal }: ModalPropsType) => {
    const viewer = useViewerModel();
    const window = useWindowDimensions();
    const { t } = useTranslation();
    const { closeModal } = useModal<ModalStackParamsType>();
    const subscriptions = useSubscriptionPlansLayout(SUBSCRIPTIONS);
    const couponStore = useCouponCodeStore();

    useAsyncInit(async () => {
      try {
        await viewer.tariffPlans.fetchPlans.run();
      } catch (error) {
        ToastService.showError(parseApiError(error).message);
      }
    });

    function closeModalByCrossIcon() {
      closeModal();
    }

    function select(item: TariffPlanCodeEnum) {
      modal.params?.onSelect(
        item,
        couponStore.getCouponForTariffPlan(item)?.id,
      );
    }

    const processedSubscriptions = useMemo(() => {
      if (couponStore.isAppliedCode) {
        return subscriptions.map((s) => {
          const coupon = couponStore.getCouponForTariffPlan(s.code);
          const currentPrice = coupon
            ? formatCurrencyToGBP(coupon.newAmount)
            : s.currentPrice;
          const regularPrice = coupon
            ? s.currentPrice
            : s.regularPrice;

          return {
            ...s,
            currentPrice,
            regularPrice,
          };
        });
      }

      return subscriptions;
    }, [subscriptions, couponStore.coupon]);

    const isError =
      Object.keys(viewer.tariffPlans.byCode).length === 0 &&
      viewer.tariffPlans.fetchPlans.isError;

    const height = window.height - 20;

    return (
      <ModalView maxHeight={height}>
        <ScrollViewContainer>
          <Separator size="$8" />

          <TopContainer>
            <Title>{t('changePlan.subtitle')}</Title>
            <IconTouchable
              onPress={closeModalByCrossIcon}
              name="close-light"
              size={24}
            />
          </TopContainer>

          <Separator size="$5" />

          <Description>{t('changePlan.description')}</Description>

          <Separator size="$5" />

          {viewer.tariffPlans.couponActivate && (
            <CouponFormContainer>
              <CouponForm
                onApply={couponStore.applyCoupon}
                isApplied={couponStore.isAppliedCode}
              />
            </CouponFormContainer>
          )}

          <Separator size="$8" />

          {viewer.tariffPlans.fetchPlans.inProgress && (
            <LoaderContainer>
              <Loader size="large" />
            </LoaderContainer>
          )}

          {!viewer.tariffPlans.fetchPlans.inProgress && isError && (
            <EmptyViewContainer>
              <EmptyResultView />
            </EmptyViewContainer>
          )}

          {!viewer.tariffPlans.fetchPlans.inProgress && !isError && (
            <SubscriptionPlansLayout>
              {processedSubscriptions.map(
                (item, itemIndex, array) => (
                  <Fragment key={itemIndex}>
                    {itemIndex === 0 && (
                      <Separator size="$8" vertical />
                    )}
                    <SubscriptionPlanItem
                      key={itemIndex}
                      item={item}
                      onPress={select}
                      style={item.style}
                      buttonText={t(
                        'chooseSubscriptionPlan.choosePlan',
                      )}
                    />
                    {array.length - 1 !== itemIndex && (
                      <Separator size="$8" vertical />
                    )}
                  </Fragment>
                ),
              )}
            </SubscriptionPlansLayout>
          )}

          <Separator size="$9" />
        </ScrollViewContainer>
      </ModalView>
    );
  },
);
